<template>
  <v-card class="mb-4">
    <v-card-title>
      {{ label }}
      <v-tooltip :text="tooltip" location="top" v-if="tooltip && tooltip.length > 0">
        <template v-slot:activator="{ props }">
          <v-icon
            v-bind="props"
            icon="mdi-information"
            size="small"
            class="ml-2"
          ></v-icon>
        </template>
      </v-tooltip>
    </v-card-title>
    <v-card-text class="text-h3">
      {{ displayCount }}
    </v-card-text>
  </v-card>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  label: {
    type: String,
    required: true
  },
  count: {
    type: Number,
    required: true
  },
  tooltip: {
    type: String,
    required: false
  }
});

// Initialize display count with the current prop value
const displayCount = ref(props.count);
const intervalId = ref(null);
const firstChangeCompleted = ref(false);

// Function to animate the count
const animateCount = (start, end) => {
  // Clear any existing animation
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }
  
  // Calculate step size and animation parameters
  const difference = end - start;
  const duration = 500; // animation duration in ms
  const framesPerSecond = 30;
  const totalFrames = duration / (500 / framesPerSecond);
  
  // Determine appropriate step size based on the difference
  // For small differences (less than 20), use smaller steps for smoother animation
  let step;
  if (Math.abs(difference) < 20) {
    step = Math.max(1, Math.abs(Math.ceil(difference / totalFrames)));
  } else {
    // For larger differences, use larger steps to make the animation more visible
    step = Math.max(1, Math.abs(Math.ceil(difference / 20)));
  }
  
  // Set direction based on whether we're counting up or down
  if (difference < 0) {
    step = -step;
  }
  
  let current = start;
  displayCount.value = current;
  
  intervalId.value = setInterval(() => {
    // Add step to current value
    current += step;
    
    // Check if we've reached or passed the target
    if ((step > 0 && current >= end) || (step < 0 && current <= end)) {
      displayCount.value = end;
      clearInterval(intervalId.value);
    } else {
      displayCount.value = current;
    }
  }, 1000 / framesPerSecond);
};

// Watch for changes in the count prop
watch(() => props.count, (newCount, oldCount) => {
  // First time the watcher runs (immediate execution)
  if (oldCount === undefined) {
    displayCount.value = newCount;
    return;
  }
  
  // No change in value
  if (newCount === oldCount) {
    return;
  }
  
  // First actual change in value
  if (!firstChangeCompleted.value) {
    displayCount.value = newCount;
    firstChangeCompleted.value = true;
    return;
  }
  
  // All subsequent changes - animate
  animateCount(oldCount, newCount);
}, { immediate: true });
</script>